import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Select, Text } from 'grommet';
import { QrCodeScan } from '@styled-icons/bootstrap';
import { CameraDevice, Html5Qrcode } from 'html5-qrcode';
import styled from 'styled-components';

const INITIAL_STATUS_MESSAGE =
	'Request camera permissions or upload a QR image file.';

const PERMISSION_READY_MESSAGE =
	'Camera access is ready. Tap Start Scanning or upload a QR image.';

/** Stable id for status text so primary actions can reference it (screen readers). */
const QR_SCANNER_STATUS_ID = 'qr-scanner-status';

/** Status lines that show the same large QR hero icon above the text */
const STATUS_MESSAGES_WITH_QR_HERO = new Set<string>([
	INITIAL_STATUS_MESSAGE,
	PERMISSION_READY_MESSAGE,
]);

interface Html5QRScannerProps {
	onScanSuccess: (value: string) => void;
}

const Html5QRScanner = ({ onScanSuccess }: Html5QRScannerProps) => {
	const scannerRef = useRef<Html5Qrcode | null>(null);
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const [statusMessage, setStatusMessage] = useState(INITIAL_STATUS_MESSAGE);
	const [isStartingCamera, setIsStartingCamera] = useState(false);
	const [isCameraActive, setIsCameraActive] = useState(false);
	const [hasCameraPermission, setHasCameraPermission] = useState(false);
	const [availableCameras, setAvailableCameras] = useState<CameraDevice[]>([]);
	const [selectedCameraId, setSelectedCameraId] = useState<string | null>(null);
	const [isSwitchingCamera, setIsSwitchingCamera] = useState(false);

	const stopCamera = useCallback(async () => {
		if (!scannerRef.current) return;
		try {
			// Prefer library state over React state to avoid stale closures and
			// unnecessary callback identity churn in startCamera / onFileSelected.
			if (scannerRef.current.isScanning) {
				await scannerRef.current.stop();
			}
		} catch (error) {
			// Ignore stop errors to avoid blocking new actions.
		} finally {
			setIsCameraActive(false);
		}
	}, []);

	const startScannerWithCameraId = useCallback(
		async (cameraId: string) => {
			if (!scannerRef.current) return;
			const scanner = scannerRef.current;
			const scanConfig = { fps: 10, qrbox: 250 };
			const onDecode = (decodedText: string) => {
				onScanSuccess(decodedText);
			};
			const onDecodeError = () => {
				// Ignore decode errors while scanning frames.
			};
			try {
				await scanner.start(cameraId, scanConfig, onDecode, onDecodeError);
			} catch (primaryError) {
				// Safari/iPad can fail when starting with camera id string; retry with explicit constraints.
				await scanner.start(
					{ deviceId: { exact: cameraId } },
					scanConfig,
					onDecode,
					onDecodeError
				);
			}
			setSelectedCameraId(cameraId);
			setIsCameraActive(true);
		},
		[onScanSuccess]
	);

	const switchCamera = useCallback(
		async (cameraId: string) => {
			if (!scannerRef.current || cameraId === selectedCameraId || isSwitchingCamera)
				return;
			setIsSwitchingCamera(true);
			setStatusMessage('Switching camera...');
			try {
				if (scannerRef.current.isScanning) {
					await scannerRef.current.stop();
					setIsCameraActive(false);
				}
        // clear() removes the video element from the DOM and resets all internal
        // library state. Without it, start() on mobile may reuse the old stream
        // instead of opening the newly selected camera.
        await scannerRef.current.clear();
        scannerRef.current = new Html5Qrcode('qr-reader');

        // iPad Safari benefits from a short handoff delay between stop/start.
        await new Promise<void>((resolve) => {
          setTimeout(resolve, 150);
        });
        await startScannerWithCameraId(cameraId);
				setStatusMessage('Camera is active. Point it at a QR code.');
			} catch (error) {
				setStatusMessage(
					'Could not switch camera. Try another device or scan from file.'
				);
			} finally {
				setIsSwitchingCamera(false);
			}
		},
		[isSwitchingCamera, selectedCameraId, startScannerWithCameraId]
	);

	const startCamera = useCallback(async () => {
		if (!scannerRef.current || isStartingCamera) return;
		setIsStartingCamera(true);
		setStatusMessage('Requesting camera permission...');
		try {
			const cameras = await Html5Qrcode.getCameras();
			if (!cameras.length) {
				setStatusMessage(
					'No camera was found. Try "Scan an Image File" to continue.'
				);
				return;
			}

			// Permission is granted once we can enumerate cameras; set before start()
			// so the button label updates even if start() fails later.
			setHasCameraPermission(true);
			setAvailableCameras(cameras);

			await stopCamera();
			await startScannerWithCameraId(cameras[0].id);
			setStatusMessage('Camera is active. Point it at a QR code.');
		} catch (error) {
			setStatusMessage(
				'Camera access is blocked or unavailable. You can scan an image file instead.'
			);
		} finally {
			setIsStartingCamera(false);
		}
	}, [isStartingCamera, startScannerWithCameraId, stopCamera]);

	const onFileSelected = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			const input = event.currentTarget;
			const file = input.files?.[0];
			if (!file || !scannerRef.current) return;
			try {
				await stopCamera();
				const decodedText = await scannerRef.current.scanFile(file, true);
				onScanSuccess(decodedText);
			} catch (error) {
				setStatusMessage(
					'Unable to read QR from image. Please try a clearer image.'
				);
			} finally {
				input.value = '';
			}
		},
		[onScanSuccess, stopCamera]
	);

	useEffect(() => {
		scannerRef.current = new Html5Qrcode('qr-reader');

		return () => {
			// React cleanup cannot be async: stop()/clear() may still be in flight when
			// the host node is removed. Fire-and-forget is the usual pattern for html5-qrcode;
			// errors are swallowed so teardown never blocks unmount.
			(async () => {
				if (!scannerRef.current) return;
				try {
					if (scannerRef.current.isScanning) {
						await scannerRef.current.stop();
					}
				} catch (error) {
					// Scanner may already be stopped; safe to ignore.
				}
				try {
					await scannerRef.current.clear();
				} catch (error) {
					// Safe cleanup fallback.
				}
				scannerRef.current = null;
			})().catch(() => undefined);
		};
	}, []);

	// If permission was already granted (e.g. user reopened modal), show correct label.
	useEffect(() => {
		let cancelled = false;
		Html5Qrcode.getCameras()
			.then((cameras) => {
				if (!cancelled && cameras.length > 0) {
					setHasCameraPermission(true);
					setStatusMessage((prev) =>
						prev === INITIAL_STATUS_MESSAGE
							? PERMISSION_READY_MESSAGE
							: prev
					);
				}
			})
			.catch(() => {
				// No permission yet — keep default label.
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const canShowPermissionReady = useMemo(
		() =>
			hasCameraPermission &&
			!isCameraActive &&
			!isStartingCamera &&
			!isSwitchingCamera,
		[hasCameraPermission, isCameraActive, isStartingCamera, isSwitchingCamera]
	);

	// When permission is ready and the UI is idle, promote the first-run hint to the ready line.
	useEffect(() => {
		if (!canShowPermissionReady) return;
		setStatusMessage((prev) =>
			prev === INITIAL_STATUS_MESSAGE ? PERMISSION_READY_MESSAGE : prev
		);
	}, [canShowPermissionReady]);

	const cameraButtonLabel = useMemo(() => {
		if (isStartingCamera) return 'Requesting Permission...';
		if (hasCameraPermission) return 'Start Scanning';
		return 'Request Camera Permissions';
	}, [hasCameraPermission, isStartingCamera]);

	const showQrHeroIcon =
		!isCameraActive &&
		!isStartingCamera &&
		!isSwitchingCamera &&
		STATUS_MESSAGES_WITH_QR_HERO.has(statusMessage);

	const cameraSelectOptions = useMemo(
		() =>
			availableCameras.map((camera) => ({
				label: camera.label?.trim() || `Camera (${camera.id.slice(0, 8)}…)`,
				value: camera.id,
			})),
		[availableCameras]
	);

	const getSelectedCameraId = (
		value: string | { value?: string } | undefined
	): string | null => {
		if (typeof value === 'string') return value;
		if (value && typeof value.value === 'string') return value.value;
		return null;
	};

	return (
		<Wrapper>
			{showQrHeroIcon && (
				<QrIconWrap align="center" justify="center">
					<ScanQrMark role="img" aria-label="QR code scan" />
				</QrIconWrap>
			)}
			<StatusText id={QR_SCANNER_STATUS_ID}>{statusMessage}</StatusText>
			{!isCameraActive && (
				<ButtonRow
					direction="row"
					align="center"
					gap="small"
					role="group"
					aria-label="QR scan actions"
				>
					<Button
						primary
						label={cameraButtonLabel}
						onClick={startCamera}
						disabled={isStartingCamera}
						aria-describedby={QR_SCANNER_STATUS_ID}
					/>
					<Button
						secondary
						label="Scan an Image File"
						onClick={() => fileInputRef.current?.click()}
						aria-describedby={QR_SCANNER_STATUS_ID}
					/>
				</ButtonRow>
			)}

			<input
				ref={fileInputRef}
				type="file"
				accept="image/*"
				onChange={onFileSelected}
				style={{ display: 'none' }}
			/>
			<div
				className={
					isCameraActive || isStartingCamera || isSwitchingCamera
						? 'scan-region-wrap'
						: 'scan-region-wrap is-hidden'
				}
			>
				<div id="qr-reader" className="scan-region" />
				{isCameraActive && cameraSelectOptions.length > 0 && (
					<CameraSelectOverlay
						align="end"
						justify="start"
						pad="small"
						background={{ color: 'white', opacity: 'strong' }}
						round="xsmall"
					>
						<Select
							size="small"
							placeholder="Camera"
							disabled={isSwitchingCamera}
							labelKey="label"
							valueKey={{ key: 'value', reduce: true }}
							options={cameraSelectOptions}
							value={selectedCameraId ?? undefined}
							onChange={({ value }) => {
								const nextCameraId = getSelectedCameraId(
									value as string | { value?: string } | undefined
								);

								if (nextCameraId) {
									switchCamera(nextCameraId).catch(() => undefined);
								}
							}}
						/>
					</CameraSelectOverlay>
				)}
			</div>
		</Wrapper>
	);
};

export default Html5QRScanner;

const Wrapper = styled(Box)`
	width: 100%;
	padding: 0;

	.scan-region-wrap {
		position: relative;
	}

	.scan-region {
		width: 100%;
		min-height: 260px;
		border: 1px solid #e0e0e0;
		border-radius: 8px;
		background: #fafafa;
		overflow: hidden;
	}

	.scan-region-wrap.is-hidden {
		display: none;
	}
`;

const CameraSelectOverlay = styled(Box)`
	position: absolute;
	top: 10px;
	right: 10px;
	z-index: 2;
	padding: 0;
	min-width: 200px;
	max-width: min(280px, 92%);
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
`;

const ButtonRow = styled(Box)`
	margin-bottom: 10px;
	justify-content: center;
	align-items: center;
`;

const StatusText = styled(Text)`
	color: #555;
	margin: 15px 0 15px;
	font-weight: 700;
	font-size: 18px;
	text-align: center;
`;

const QrIconWrap = styled(Box)`
	margin: 25px 0 0;
`;

const ScanQrMark = styled(QrCodeScan)`
	width: 112px;
	height: 112px;
	color: #444;
	opacity: 0.92;
	margin-bottom: 20px;
`;
